import { useMarkConversationSeen } from '@/hooks/actions/use-mark-conversation-seen';
import { useSendMessage } from '@/hooks/actions/use-send-message';
import {
  getChronologicalConversationFeed,
  groupConversationFeedByDate,
  useConversationMessagesQuery,
} from '@/hooks/queries/use-conversation-messages-query';
import {
  getConversationModerationReason,
  getConversationReplyChannels,
  getDefaultConversationChannel,
} from '@/lib/conversation-selectors';
import { Box, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import {
  DashboardConversationFeedItemType,
  type Conversation,
  type DashboardMessageFeedItem,
} from '@fluenceur/types';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from 'framer-motion';
import ConversationDayDivider from './ConversationDayDivider';
import ConversationInput from './ConversationInput';
import { CreatorTypingIndicator } from './CreatorTypingIndicator';
import api from '@/libs/axiosInstance';
import MessageBubble from './MessageBubble';
import SystemEventEntry from './SystemEventEntry';

interface ConversationProps {
  conversation: Conversation;
  /**
   * `panel` sits in a drawer under its own header: no room kept for the page's
   * floating participant header. `page` (default) is the Inbox thread.
   */
  variant?: 'page' | 'panel';
  /** Hands an application marker back to the surface that shows applications. */
  onOpenApplication?: (applicationId: string) => void;
  /** Names the applicant on application markers, when the caller knows her better than the thread. */
  applicantName?: string | null;
}

const isSameMessageCluster = (
  left: DashboardMessageFeedItem | undefined,
  right: DashboardMessageFeedItem
) => left?.isBrandSender === right.isBrandSender && left?.channel?.id === right.channel?.id;

const Conversation = ({
  conversation,
  variant = 'page',
  onOpenApplication,
  applicantName,
}: ConversationProps) => {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const markedConversationRef = useRef<string | null>(null);
  const previousNewestMessageIdRef = useRef<string | null>(null);
  const pendingScrollRestoreRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [liveMessage, setLiveMessage] = useState<string | null>(null);
  const sendMessageMutation = useSendMessage(conversation.brandId);
  const markConversationSeenMutation = useMarkConversationSeen(conversation.brandId);
  const replyChannels = getConversationReplyChannels(conversation);
  const defaultChannel = getDefaultConversationChannel(conversation);
  const selectedChannel =
    replyChannels.find((channel) => channel.id === selectedChannelId) ?? defaultChannel;
  const moderationReason = getConversationModerationReason(conversation);
  const lastTypingSignalRef = useRef(0);
  const isNativeReply = selectedChannel?.type === 'native_app';
  // The creator space shows "Camille écrit...": one signal every three seconds at most.
  const handleTyping = useCallback(() => {
    if (!isNativeReply) return;
    const now = Date.now();
    if (now - lastTypingSignalRef.current < 3000) return;
    lastTypingSignalRef.current = now;
    void api.post(`/dashboard/conversations/${conversation.id}/typing`).catch(() => null);
  }, [conversation.id, isNativeReply]);

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } =
    useConversationMessagesQuery(conversation.brandId, conversation.id);
  const chronologicalFeed = useMemo(
    () => getChronologicalConversationFeed(data?.pages),
    [data?.pages]
  );
  const feedGroups = useMemo(
    () => groupConversationFeedByDate(chronologicalFeed),
    [chronologicalFeed]
  );

  useEffect(() => {
    markedConversationRef.current = null;
    previousNewestMessageIdRef.current = null;
    pendingScrollRestoreRef.current = null;
    setSelectedChannelId(defaultChannel?.id ?? null);
  }, [conversation.id, defaultChannel?.id]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  useEffect(() => {
    const newest = chronologicalFeed.at(-1);
    if (!newest) return;
    if (previousNewestMessageIdRef.current !== newest.id) {
      scrollToBottom(previousNewestMessageIdRef.current && !reducedMotion ? 'smooth' : 'auto');
      if (
        previousNewestMessageIdRef.current &&
        newest.itemType === DashboardConversationFeedItemType.MESSAGE &&
        !newest.isBrandSender
      ) {
        setLiveMessage(t('messages.thread.newMessage'));
      }
      previousNewestMessageIdRef.current = newest.id;
    }
    if (markedConversationRef.current !== conversation.id) {
      markedConversationRef.current = conversation.id;
      markConversationSeenMutation.mutate(conversation.id);
    }
  }, [
    chronologicalFeed,
    conversation.id,
    markConversationSeenMutation,
    reducedMotion,
    scrollToBottom,
    t,
  ]);

  const fetchOlderMessages = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasNextPage || isFetchingNextPage) return;
    pendingScrollRestoreRef.current = {
      scrollHeight: container.scrollHeight,
      scrollTop: container.scrollTop,
    };
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!pendingScrollRestoreRef.current || isFetchingNextPage) return;
    const restore = pendingScrollRestoreRef.current;
    pendingScrollRestoreRef.current = null;
    requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      if (container)
        container.scrollTop = container.scrollHeight - restore.scrollHeight + restore.scrollTop;
    });
  }, [data?.pages.length, isFetchingNextPage]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (container.scrollTop < 120) fetchOlderMessages();
    if (container.scrollHeight - container.scrollTop - container.clientHeight < 50) {
      markConversationSeenMutation.mutate(conversation.id);
    }
  }, [conversation.id, fetchOlderMessages, markConversationSeenMutation]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!selectedChannel) throw new Error('No communication channel is available');
      await sendMessageMutation.sendMessage({
        conversationId: conversation.id,
        channel: selectedChannel,
        content,
      });
    },
    [conversation.id, selectedChannel, sendMessageMutation]
  );

  if (isLoading && !feedGroups.length) {
    return (
      <Flex justify="center" align="center" flex="1">
        <Spinner size="lg" />
      </Flex>
    );
  }
  if (error) {
    return (
      <Flex justify="center" align="center" flex="1">
        <Text color="red.500">{error.message}</Text>
      </Flex>
    );
  }

  return (
    <Box position="relative" h="100%" flex="1" bg="white">
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        position="absolute"
        inset={0}
        overflowY="auto"
        px={variant === 'panel' ? 4 : 6}
        pt={variant === 'panel' ? 4 : '88px'}
        pb="160px"
        role="log"
        aria-live="off"
        aria-label={t('messages.thread.log')}
      >
        {!feedGroups.length ? (
          <Flex justify="center" align="center" h="full">
            <Text color="gray.500">{t('messages.thread.empty')}</Text>
          </Flex>
        ) : (
          <VStack gap={6} align="stretch">
            {hasNextPage && (
              <Flex justify="center" minH="32px">
                {isFetchingNextPage && <Spinner size="sm" />}
              </Flex>
            )}
            {feedGroups.map((group) => (
              <Box key={group.date}>
                <ConversationDayDivider date={group.date} />
                <VStack gap={1} align="stretch">
                  {group.items.map((item, index) => {
                    if (item.itemType === DashboardConversationFeedItemType.SYSTEM_EVENT) {
                      return (
                        <Box key={`event:${item.id}`} py={1}>
                          <SystemEventEntry
                            event={item}
                            participantName={applicantName ?? conversation.participant?.name ?? null}
                            onOpenApplication={onOpenApplication}
                          />
                        </Box>
                      );
                    }

                    const previousItem = group.items[index - 1];
                    const nextItem = group.items[index + 1];
                    const previousMessage =
                      previousItem?.itemType === DashboardConversationFeedItemType.MESSAGE
                        ? previousItem
                        : undefined;
                    const nextMessage =
                      nextItem?.itemType === DashboardConversationFeedItemType.MESSAGE
                        ? nextItem
                        : undefined;
                    const previousChannelMessage = group.items
                      .slice(0, index)
                      .reverse()
                      .find(
                        (candidate): candidate is DashboardMessageFeedItem =>
                          candidate.itemType === DashboardConversationFeedItemType.MESSAGE
                      );
                    const channelChanged =
                      !!previousChannelMessage &&
                      previousChannelMessage.channel?.id !== item.channel?.id;

                    return (
                      <Box key={`message:${item.id}`}>
                        {channelChanged && item.channel && (
                          <Text
                            alignSelf="center"
                            textAlign="center"
                            fontSize="xs"
                            color="gray.500"
                            py={2}
                          >
                            {t(`messages.channel.${item.channel.type}`)}
                          </Text>
                        )}
                        <MessageBubble
                          message={item}
                          isSelf={item.isBrandSender}
                          participant={conversation.participant ?? undefined}
                          isFirstInGroup={!isSameMessageCluster(previousMessage, item)}
                          isLastInGroup={!isSameMessageCluster(nextMessage, item)}
                        />
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </VStack>
        )}
      </Box>
      <Box srOnly aria-live="polite" aria-atomic="true">
        {liveMessage}
      </Box>
      {replyChannels.length ? (
        <Box
          position="absolute"
          bottom={0}
          left={4}
          right={4}
          pb={4}
          bg="white/92"
          backdropFilter="blur(12px)"
        >
          <CreatorTypingIndicator
            conversationId={conversation.id}
            creatorName={conversation.participant?.name ?? ''}
            newestMessageId={chronologicalFeed.at(-1)?.id ?? null}
          />
          <ConversationInput
            onTyping={handleTyping}
            onSendMessage={handleSendMessage}
            isLoading={sendMessageMutation.isPending}
            channels={replyChannels}
            selectedChannelId={selectedChannel?.id ?? null}
            onChannelChange={setSelectedChannelId}
          />
        </Box>
      ) : (
        <Flex
          position="absolute"
          bottom={0}
          left={4}
          right={4}
          p={4}
          justify="center"
          bg="white/92"
        >
          <Text color="gray.600">
            {moderationReason
              ? t('messages.thread.blocked', {
                  reason: t(`messages.moderationReason.${moderationReason}`),
                })
              : t('messages.thread.unavailable')}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

export default memo(Conversation);
